import type {CopilotChatModel} from './copilot-chat-types'

/**
 * Model tier configuration
 */
export interface ModelTierConfig {
  lowTier: CopilotChatModel[]
  midTier: CopilotChatModel[]
  highTier: CopilotChatModel[]
  base: CopilotChatModel[]
  preview: CopilotChatModel[]
}

/**
 * Low tier model exceptions - models that should be in low tier despite billing multiplier
 */
const LOW_TIER_MODEL_EXCEPTIONS = ['o3-mini', 'Gemini 2.0 Flash']

/**
 * Determines if a model belongs to the low tier based on category or billing
 */
function isLowTierModel(model: CopilotChatModel, useCategoryField: boolean): boolean {
  // If using category field and it's present, use it
  if (useCategoryField && model.model_picker_category !== undefined) {
    return model.model_picker_category === 'lightweight'
  }

  // Fallback to billing multiplier logic
  return (
    (model.billing &&
      ((model.billing?.multiplier < 1 && model.billing?.multiplier !== 0) ||
        LOW_TIER_MODEL_EXCEPTIONS.includes(model.displayName))) ||
    false
  )
}

/**
 * Determines if a model belongs to the mid tier based on category or billing
 */
function isMidTierModel(model: CopilotChatModel, useCategoryField: boolean): boolean {
  // If using category field and it's present, use it
  if (useCategoryField && model.model_picker_category !== undefined) {
    return model.model_picker_category === 'versatile'
  }

  // Fallback to billing multiplier logic
  return (
    (model.billing &&
      (model.billing?.multiplier === 1 || model.billing?.multiplier === 0) &&
      !LOW_TIER_MODEL_EXCEPTIONS.includes(model.displayName)) ||
    false
  )
}

/**
 * Determines if a model belongs to the high tier based on category or billing
 */
function isHighTierModel(model: CopilotChatModel, useCategoryField: boolean): boolean {
  // If using category field and it's present, use it
  if (useCategoryField && model.model_picker_category !== undefined) {
    return model.model_picker_category === 'powerful'
  }

  // Fallback to billing multiplier logic
  return (model.billing && model.billing?.multiplier > 1) || false
}

/**
 * Checks if models have diversity in their categories (either by category field or billing)
 */
export function hasModelCategoryDiversity(models: CopilotChatModel[], useCategoryField: boolean): boolean {
  if (useCategoryField) {
    // Check for category diversity or billing diversity as fallback
    const categoryDiversity =
      models.some(m => m.model_picker_category !== undefined) &&
      new Set(models.filter(m => m.model_picker_category !== undefined).map(m => m.model_picker_category)).size > 1

    const billingDiversity =
      models.some(m => m.billing?.multiplier !== undefined) &&
      new Set(models.filter(m => m.billing?.multiplier !== undefined).map(m => m.billing?.multiplier)).size > 1

    return categoryDiversity || billingDiversity
  } else {
    // Original logic: check for billing multiplier diversity
    return (
      models.some(m => m.billing?.multiplier !== undefined) &&
      new Set(models.filter(m => m.billing?.multiplier !== undefined).map(m => m.billing?.multiplier)).size > 1
    )
  }
}

/**
 * Categorizes models into tiers for the model picker
 */
export function categorizeModels(
  models: CopilotChatModel[],
  useCategoryField: boolean,
  shouldShowCategories: boolean,
): ModelTierConfig {
  if (!shouldShowCategories) {
    return {
      lowTier: [],
      midTier: [],
      highTier: [],
      base: models.filter(m => !m.preview),
      preview: models.filter(m => m.preview),
    }
  }

  return {
    lowTier: models.filter(m => isLowTierModel(m, useCategoryField)),
    midTier: models.filter(m => isMidTierModel(m, useCategoryField)),
    highTier: models.filter(m => isHighTierModel(m, useCategoryField)),
    base: [],
    preview: [],
  }
}
